import clsx from 'clsx';
import { useAuth } from 'context/auth';
import Image from 'next/image';
import Link from 'next/link';
import { CSSProperties, useState } from 'react';

import { Icon } from 'common/icon';
import { Button } from 'common/new-button';
import { Breadcrumbs } from 'components/bredcrambs';
import { LoginModal } from 'components/modal/login';
import { reviewsCasino, reviewsSlot } from 'constants/routes-breadcrumbs';
import { useLoginModal } from 'hooks';

import { YoutubeFrame } from 'components/iframe-youtube';
import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { CasinoReviewModal } from 'components/modal/casino-review';
import { CasinoSlotReviewModal } from 'components/modal/casino-slot-review';
import { SlotReviewModal } from 'components/modal/slot-review';
import { ICasino, ISlot } from 'constants/types';
import { BreadcrumbsProps } from 'constants/routes-breadcrumbs';

import styles from './styles.module.css';
import { Routes } from 'constants/routes';
import { getItemIdentifier } from 'utils/slug';

type Props = {
  link?: string;
  slot?: ISlot;
  name: string;
  casino?: ICasino;
  logoUrl?: string;
  imageUrl: string;
  videoLink?: string;
  type: 'casino' | 'slot' | 'casino-slots';
  reviewsCount: string | number;
};

export const ReviewHeader = ({
  type,
  link,
  slot,
  name,
  casino,
  logoUrl,
  imageUrl,
  videoLink,
  reviewsCount,
}: Props) => {
  const { isLoggedIn } = useAuth();

  const [isModalVisible, setModalVisible] = useState<boolean>(false);

  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();

  const backgroundStyle: CSSProperties = {
    background: `url(${imageUrl}) top/cover no-repeat`,
    opacity: '0.6999999881',
    WebkitFilter: 'blur(25px)',
    filter: 'blur(25px)',
    position: 'absolute',
    top: '0',
    left: '0',
    height: '410px',
    width: '100%',
    zIndex: '-1',
  };

  return (
    <section className={styles.review_block}>
      <div style={backgroundStyle} />
      <div className={styles.container_s}>
        <div className={styles.review_block__row}>
          <div className={styles.review_block__card}>
            {videoLink ? (
              <YoutubeFrame link={videoLink} />
            ) : (
              <Link
                target={link ? '_blank' : ''}
                href={link ? link : '#'}
                className={clsx(styles.review_block__card_wrapper, {
                  [styles?.['default-cursor']]: !link?.length,
                })}
              >
                {link && <div className={styles.casino_card__cover} />}
                <LogoOverlayWrapper>
                  <LogoOverlay
                    width={180}
                    height={270}
                    alt={name}
                    image={logoUrl}
                  />

                  <Image
                    style={{
                      minWidth: '180px',
                      minHeight: '270px',
                    }}
                    width={180}
                    height={180}
                    alt={name}
                    src={imageUrl ?? ''}
                  />
                </LogoOverlayWrapper>
              </Link>
            )}
          </div>
          <div className={styles.review_block__info}>
            <Breadcrumbs
              enableAbsoluteOnMobile
              items={
                type === 'slot'
                  ? [
                      ...reviewsSlot,
                      {
                        id: 2,
                        path: Routes.slotPage(getItemIdentifier(slot)),
                        title: slot?.name,
                      } as BreadcrumbsProps,
                    ]
                  : [
                      ...reviewsCasino,
                      {
                        id: 2,
                        path: Routes.casinoPage(getItemIdentifier(casino)),
                        title: casino?.name,
                      } as BreadcrumbsProps,
                      {
                        id: 3,
                        path: `#`,
                        title:
                          type === 'casino-slots'
                            ? 'Slots RTP Reviews'
                            : 'Reviews',
                      } as BreadcrumbsProps,
                    ]
              }
            />
            <div className={styles.review_block__title}>
              <h1>
                {name}
                <br />
                {reviewsCount} {type === 'slot' && 'casinos RTP reviews'}
                {type === 'casino' && 'casino reviews'}
                {type === 'casino-slots' && 'slots RTP reviews'}
              </h1>
            </div>
            <div
              className={clsx(
                styles.review_block__add_slot,
                styles.add_slot,
                styles.popup
              )}
            >
              <Button
                icon={<Icon.SerachPlus />}
                text={
                  (type === 'slot' && 'Add slot RTP review') ||
                  (type === 'casino' && 'Write a casino review') ||
                  (type === 'casino-slots' && 'Add slot RTP review') ||
                  ''
                }
                onClick={() =>
                  isLoggedIn ? setModalVisible(true) : handleVisibleLoginModal()
                }
                className={clsx(styles.add_slot__btn, styles.popup_open)}
              />
            </div>
            <Link
              href={
                (type === 'slot' && Routes.slotPage(getItemIdentifier(slot))) ||
                (type === 'casino' &&
                  Routes.casinoPage(getItemIdentifier(casino))) ||
                (type === 'casino-slots' &&
                  Routes.casinoPage(getItemIdentifier(casino))) ||
                ''
              }
              className={styles.review_block__visit}
            >
              {type === 'slot' && <span>{'visit slot page'}</span>}
              {type === 'casino' && <span>{'visit casino page'}</span>}
              {type === 'casino-slots' && <span>{'visit casino page'}</span>}
              <Icon.Arrow width={20} height={20} />
            </Link>
          </div>
        </div>
      </div>

      <LoginModal
        isVisible={isLoginVisible}
        onClose={handleCloseLoginModal}
        confirmOnClick={handleConfirmLoginModal}
      />

      {type === 'casino' && casino && (
        <CasinoReviewModal
          casino={casino}
          isVisible={isModalVisible}
          setVisible={setModalVisible}
        />
      )}

      {type === 'slot' && (
        <SlotReviewModal
          slot={slot}
          isVisible={isModalVisible}
          setVisible={setModalVisible}
        />
      )}

      {type === 'casino-slots' && (
        <CasinoSlotReviewModal
          casino={casino}
          isVisible={isModalVisible}
          setVisible={setModalVisible}
        />
      )}
    </section>
  );
};
